import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import type { EntityId } from '../api/entityId';

export type DocumentPresenceMode = 'Viewing' | 'Editing';
export type WorkspaceTreeChangeKind = 'Created' | 'Renamed' | 'Moved' | 'Deleted';
export type WorkspaceTreeEntityKind = 'Project' | 'Folder' | 'Document';

export interface DocumentPresenceParticipantMessage {
  userId: unknown;
  userName: string;
  mode: DocumentPresenceMode;
  lastChangedAt?: unknown;
}

export interface DocumentPresenceChangedMessage {
  projectId: unknown;
  documentId: unknown;
  participants: DocumentPresenceParticipantMessage[];
}

export interface DocumentPresenceSnapshotMessage {
  documents: DocumentPresenceChangedMessage[];
}

export interface DocumentContentSavedMessage {
  projectId: unknown;
  documentId: unknown;
  contentVersion: unknown;
  savedAt: unknown;
}

export interface DocumentCommentCreatedMessage {
  projectId: unknown;
  documentId: unknown;
  commentId: unknown;
  authorUserId: unknown;
}

export interface ApplicationVersionMessage {
  version: string;
}

export interface WorkspaceTreeChangedMessage {
  kind: WorkspaceTreeChangeKind;
  entityKind: WorkspaceTreeEntityKind;
  projectId: unknown;
  entityId: unknown;
  parentFolderId?: unknown;
  name?: string | null;
  title?: string | null;
  contentVersion?: unknown;
  canEdit?: boolean | null;
}

export interface WorkspaceRealtimeHandlers {
  applicationVersion(message: ApplicationVersionMessage): void;
  documentCommentCreated(message: DocumentCommentCreatedMessage): void;
  documentPresenceSnapshot(message: DocumentPresenceSnapshotMessage): void;
  documentPresenceChanged(message: DocumentPresenceChangedMessage): void;
  documentContentSaved(message: DocumentContentSavedMessage): void;
  workspaceTreeChanged(message: WorkspaceTreeChangedMessage): void;
  connected(): void;
  reconnected(): void;
  getOpenDocumentIds(): EntityId[];
  onError(message: string): void;
}

const retryDelaysMs = [0, 2000, 10000, 30000];
let connection: HubConnection | null = null;
let heartbeatTimer: number | null = null;
let retryTimer: number | null = null;
let startPromise: Promise<void> | null = null;
let activeHandlers: WorkspaceRealtimeHandlers | null = null;
let retryCount = 0;
let lifecycleVersion = 0;
let shouldRun = false;

export async function startWorkspaceRealtime(handlers: WorkspaceRealtimeHandlers): Promise<void> {
  activeHandlers = handlers;
  shouldRun = true;
  if (connection == null) {
    connection = createConnection();
  }

  if (connection.state === HubConnectionState.Connected
    || connection.state === HubConnectionState.Connecting
    || connection.state === HubConnectionState.Reconnecting) {
    return;
  }

  if (startPromise != null) {
    await startPromise;
    return;
  }

  const version = lifecycleVersion;
  const pendingStart = startConnection(version);
  startPromise = pendingStart;
  try {
    await pendingStart;
  } finally {
    if (startPromise === pendingStart) {
      startPromise = null;
    }
  }
}

export async function stopWorkspaceRealtime(): Promise<void> {
  shouldRun = false;
  lifecycleVersion++;
  retryCount = 0;
  clearRetry();
  stopHeartbeat();
  activeHandlers = null;
  const current = connection;
  connection = null;
  startPromise = null;
  if (current != null && current.state !== HubConnectionState.Disconnected) {
    await current.stop();
  }
}

export async function subscribeWorkspaceRealtime(): Promise<void> {
  if (connection?.state !== HubConnectionState.Connected) {
    return;
  }

  await connection.invoke('SubscribeWorkspace');
}

export async function syncWorkspaceRealtimeOpenDocuments(documentIds: EntityId[]): Promise<void> {
  if (connection?.state !== HubConnectionState.Connected) {
    return;
  }

  await connection.invoke('SyncOpenDocuments', [...new Set(documentIds)]);
}

export function reportWorkspaceDocumentChanged(documentId: EntityId): void {
  if (connection?.state !== HubConnectionState.Connected) {
    return;
  }

  void connection.invoke('ReportDocumentChanged', documentId).catch(() => {
    activeHandlers?.onError('Realtime document presence is unavailable.');
  });
}

function createConnection(): HubConnection {
  const created = new HubConnectionBuilder()
    .withUrl('/hubs/workspace-realtime')
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: retryContext => retryDelay(retryContext.previousRetryCount)
    })
    .configureLogging(LogLevel.Warning)
    .build();

  created.on('ApplicationVersion', message => activeHandlers?.applicationVersion(message));
  created.on('DocumentCommentCreated', message => activeHandlers?.documentCommentCreated(message));
  created.on('DocumentPresenceSnapshot', message => activeHandlers?.documentPresenceSnapshot(message));
  created.on('DocumentPresenceChanged', message => activeHandlers?.documentPresenceChanged(message));
  created.on('DocumentContentSaved', message => activeHandlers?.documentContentSaved(message));
  created.on('WorkspaceTreeChanged', message => activeHandlers?.workspaceTreeChanged(message));
  created.onreconnected(async () => {
    retryCount = 0;
    try {
      await recoverConnection(true);
    } catch {
      activeHandlers?.onError('Unable to restore realtime workspace updates.');
      if (created === connection && shouldRun) {
        await created.stop();
      }
    }
  });
  created.onclose(error => {
    stopHeartbeat();
    if (!shouldRun || created !== connection) {
      return;
    }

    if (error != null) {
      activeHandlers?.onError('Realtime workspace updates are unavailable.');
    }

    scheduleStartRetry(lifecycleVersion);
  });
  return created;
}

async function startConnection(version: number): Promise<void> {
  const current = connection;
  if (!shouldRun || current == null || version !== lifecycleVersion) {
    return;
  }

  try {
    await current.start();
    if (!shouldRun || current !== connection || version !== lifecycleVersion) {
      await current.stop();
      return;
    }

    await recoverConnection(false);
    retryCount = 0;
  } catch {
    if (!shouldRun || current !== connection || version !== lifecycleVersion) {
      return;
    }

    activeHandlers?.onError('Realtime workspace updates are unavailable.');
    if (current.state !== HubConnectionState.Disconnected) {
      await current.stop();
    }

    scheduleStartRetry(version);
  }
}

async function recoverConnection(reconnected: boolean): Promise<void> {
  await subscribeWorkspaceRealtime();
  await syncWorkspaceRealtimeOpenDocuments(activeHandlers?.getOpenDocumentIds() ?? []);
  startHeartbeat();
  activeHandlers?.connected();
  if (reconnected) {
    activeHandlers?.reconnected();
  }
}

function scheduleStartRetry(version: number): void {
  if (!shouldRun || retryTimer != null || version !== lifecycleVersion) {
    return;
  }

  const delay = retryDelay(retryCount++);
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    const handlers = activeHandlers;
    if (!shouldRun || handlers == null || version !== lifecycleVersion) {
      return;
    }

    void startWorkspaceRealtime(handlers);
  }, delay);
}

function retryDelay(previousRetryCount: number): number {
  return retryDelaysMs[Math.min(previousRetryCount, retryDelaysMs.length - 1)];
}

function startHeartbeat(): void {
  stopHeartbeat();
  heartbeatTimer = window.setInterval(() => {
    void syncWorkspaceRealtimeOpenDocuments(activeHandlers?.getOpenDocumentIds() ?? []);
  }, 30000);
}

function stopHeartbeat(): void {
  if (heartbeatTimer == null) {
    return;
  }

  window.clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}

function clearRetry(): void {
  if (retryTimer == null) {
    return;
  }

  window.clearTimeout(retryTimer);
  retryTimer = null;
}
